import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskReveal, Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/api";

const CHAPTERS = [
  { n: "01", title: "Design that endures", body: "We strip away the unnecessary to reveal the essential — line, proportion, and light. Every VYRON silhouette is drawn to feel timeless, never trend-led." },
  { n: "02", title: "Premium craftsmanship", body: "Italian acetate, titanium cores, and hand-finished detailing. Each pair is inspected by our artisans before it ever reaches you." },
  { n: "03", title: "For everyone who sees differently", body: "Men and women who value confidence, fashion, and performance. Style has no gender — only point of view." },
  { n: "04", title: "Crafted in India, worn worldwide", body: "From our studio in Chandigarh, Mohali to doorsteps across the globe. Small batches, exacting standards, delivered with care." },
];

export default function About() {
  useSEO("About", "VYRON is a premium eyewear brand based in Chandigarh, Mohali, India, creating luxury sunglasses that combine modern design, premium craftsmanship, and everyday comfort.");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <div data-testid="about-page">
      <section ref={heroRef} className="relative h-[75vh] flex items-end overflow-hidden">
        <motion.img style={{ y }} src="/assets/vyron/img1.jpeg"
          alt="VYRON Eclipse Amber" className="absolute inset-0 w-full h-full object-cover object-center scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10 pb-14">
          <p className="text-xs uppercase tracking-[0.4em] text-black/60 mb-5">Our Story</p>
          <h1 className="font-display text-6xl md:text-9xl tracking-[-0.04em] leading-[0.85]">
            <MaskReveal>We see</MaskReveal>
            <MaskReveal delay={0.12}>differently.</MaskReveal>
          </h1>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-24 md:pt-32">
        <Reveal>
          <p className="font-display text-3xl md:text-5xl tracking-tight leading-[1.15]" data-testid="about-statement">
            VYRON is a premium eyewear brand based in Chandigarh, Mohali, India. We create luxury
            sunglasses that combine modern design, premium craftsmanship, and everyday comfort. Our
            mission is to deliver stylish, high-quality eyewear for people who value confidence,
            fashion, and performance.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="space-y-20 md:space-y-32">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
              <Reveal className="md:col-span-4">
                <span className="font-display text-[20vw] md:text-[9vw] leading-none text-black/10 block">{c.n}</span>
              </Reveal>
              <Reveal delay={0.1} className={`md:col-span-6 ${i % 2 ? "md:col-start-7" : "md:col-start-6"}`}>
                <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight mb-5">{c.title}</h2>
                <p className="text-muted-foreground leading-relaxed text-lg max-w-lg">{c.body}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-24 text-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight mb-8">Ready to find your frame?</h2>
          <Link to="/collections" data-testid="about-shop-btn"
            className="inline-block bg-black text-white rounded-full px-10 py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">
            Explore collections
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
